import { useMemo } from 'react'
import { LinkifyIt } from 'linkify-it'
import tlds from 'tlds'

const BANNED_PROTOCOLS = new Set(['javascript:', 'data:'])
const BANNED_TLDS = new Set(['bat', 'cmd', 'dll', 'exe', 'md', 'scr', 'zip'])

// linkify-it v6 defaults fuzzyLink to false; keep the v5 matching behavior explicit
export const linkify = new LinkifyIt({
  fuzzyLink: true,
  fuzzyEmail: true,
  fuzzyIP: false,
  tlds: tlds.filter((tld) => !BANNED_TLDS.has(tld)),
})

const tryOrNull = <T>(fn?: (() => T) | null): T | null => {
  if (!fn) {
    return null
  }

  try {
    return fn()
  } catch {
    return null
  }
}

type BuildUrlOptions = {
  /**
   * If true we won't try too hard to get a valid URL.
   */
  strict?: boolean
}

export function buildUrl(
  url: URL | string,
  { strict = false }: BuildUrlOptions = {}
): URL | null {
  const urlString = typeof url === 'string' ? url : url.toString()

  const strategies = [
    () => new URL(url),
    // Only add https: prefix for protocol-relative URLs (starting with //)
    strict === false && urlString.startsWith('//')
      ? () => new URL(`https:${url}`)
      : null,
    // If relative URL starting with /, we build one based on current origin
    strict === false &&
    !urlString.includes('://') &&
    !urlString.startsWith('//') &&
    urlString.startsWith('/')
      ? () => new URL(url, window.location?.origin)
      : null,
    // For URLs without protocol, try adding https://
    strict === false &&
    !urlString.includes('://') &&
    !urlString.startsWith('//')
      ? () => new URL(`https://${url}`)
      : null,
    // URL is completely broken, we rebuilt one based on current origin
    strict === false ? () => new URL(url, window.location?.origin) : null,
  ]

  for (const strategy of strategies) {
    const result = tryOrNull(strategy)

    if (result) {
      return result
    }
  }

  return null
}

const PROTOCOL_SANITY_CHECK = /^([\w-]+:\/\/|mailto:\w|tel:[\w+])/i

export function toSafeUrl(
  url: URL | string | null | undefined,
  options?: BuildUrlOptions
) {
  if (!url) {
    return null
  }

  if (
    typeof url === 'string' &&
    options?.strict &&
    !PROTOCOL_SANITY_CHECK.test(url)
  ) {
    return null
  }

  const maybeSafeUrl: URL | null = buildUrl(url, options)

  if (!maybeSafeUrl) {
    return null
  }

  if (!BANNED_PROTOCOLS.has(maybeSafeUrl.protocol)) {
    return maybeSafeUrl
  }
  return null
}

export function useSafeUrl(url: URL | string | null | undefined) {
  return useMemo(() => toSafeUrl(url), [url])
}
